import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password, remember);
      toast.success("Welcome back!");
      navigate(localStorage.getItem("zeeptel_skip_connect") === "1" ? "/app" : "/connect");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Log in to your ZEEPTEL account">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field label="Email" type="email" value={email} onChange={setEmail} testid="login-email" placeholder="you@example.com" />
        <Field label="Password" type="password" value={password} onChange={setPassword} testid="login-password" placeholder="••••••••" />
        <div className="flex items-center justify-between -mt-1">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              data-testid="login-remember"
              className="h-4 w-4 rounded border-black/20 accent-[#1E3A8A] cursor-pointer"
            />
            <span className="text-xs text-zinc-600 font-medium">Keep me logged in</span>
          </label>
          <Link to="/forgot-password" className="text-xs text-primary font-semibold hover:underline" data-testid="forgot-password-link">
            Forgot password?
          </Link>
        </div>
        {error && <p className="text-sm text-destructive font-medium" data-testid="login-error">{error}</p>}
        <button
          type="submit"
          disabled={loading}
          data-testid="login-submit"
          className="w-full h-12 rounded-xl bg-primary text-white font-semibold hover:bg-[#1E40AF] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Log in
        </button>
      </form>
      <p className="text-sm text-zinc-500 text-center mt-6">
        New here?{" "}
        <Link to="/register" className="text-primary font-semibold" data-testid="goto-register">
          Create an account
        </Link>
      </p>
    </AuthShell>
  );
}

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex flex-col items-center mb-8">
          <img src="/branding/zanitel_logo.png" alt="Zeeptel" className="h-12 w-auto" />
          <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-zinc-400 mt-1.5">connecting the world</span>
        </Link>
        <div className="bg-white border border-black/10 rounded-2xl p-8 shadow-sm">
          <h1 className="font-heading text-2xl font-bold tracking-tight">{title}</h1>
          <p className="text-zinc-500 text-sm mt-1 mb-6">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export function Field({ label, type = "text", value, onChange, testid, placeholder }) {
  const [show, setShow] = useState(false);
  const isPassword = type === "password";
  return (
    <div>
      <label className="block text-sm font-semibold mb-1.5">{label}</label>
      <div className="relative">
        <input
          type={isPassword && show ? "text" : type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required
          data-testid={testid}
          className={`w-full bg-zinc-50 border border-black/10 rounded-xl px-4 py-3 text-base focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all ${isPassword ? "pr-12" : ""}`}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            aria-label={show ? "Hide password" : "Show password"}
            data-testid={`${testid}-toggle-visibility`}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
          >
            {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          </button>
        )}
      </div>
    </div>
  );
}
